import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import itkLogo from "../assets/itk_logo.png";
import Header from "../components/Header";
import {
  Settings,
  Users,
  BookOpen,
  ChevronDown,
} from "lucide-react";

import {
  importUsersFromFile,
  findStudentBySchoolAndNumber,
  updateStudentByUid,
  getSchoolsForDropdown,
} from "../web/src/services/importService";

const PRIMARY_BLUE = "#1565C0";
const HEADER_HEIGHT = 72;
const HEADER_TOP_OFFSET = 12;
const MOBILE_HEADER_CLEARANCE = HEADER_HEIGHT + HEADER_TOP_OFFSET + 16;
const MOBILE_MENU_TOP = HEADER_HEIGHT + HEADER_TOP_OFFSET + 8;

const BG = "#f4f7fb";
const CARD_BG = "#ffffff";
const BORDER = "#dbe3ee";
const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#64748b";

const TYPO = {
  fontFamily:
    '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  sizes: {
    xs: 12,
    sm: 13,
    md: 14,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  lineHeights: {
    tight: 1.25,
    normal: 1.45,
    relaxed: 1.6,
  },
  letterSpacing: {
    tight: "-0.01em",
    normal: "0",
  },
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

export default function UserEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [selectedSchoolCode, setSelectedSchoolCode] = useState("");
  const [studentNumber, setStudentNumber] = useState("");

  const [searchLoading, setSearchLoading] = useState(false);
  const [studentError, setStudentError] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);

  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");

  const menuGroups = useMemo(
    () => [
      {
        key: "yonetim-paneli",
        title: "Yönetim Paneli",
        icon: Settings,
        children: [
          {
            label: "Kullanıcı Düzenleme",
            path: "/user-edit",
            icon: Users,
          },
          {
            label: "Müfredat Düzenleme",
            path: "/curriculum-edit",
            icon: BookOpen,
          },
        ],
      },
    ],
    []
  );

  const findPathMatch = (groups, pathname) => {
    for (const group of groups) {
      for (const child of group.children || []) {
        if (child.path === pathname) {
          return { groupKey: group.key, childLabel: child.label };
        }
      }
    }
    return null;
  };

  const initialMatch = findPathMatch(menuGroups, location.pathname);

  const [openGroups, setOpenGroups] = useState(() => ({
    "yonetim-paneli": true,
    ...(initialMatch?.groupKey ? { [initialMatch.groupKey]: true } : {}),
  }));

  useEffect(() => {
    async function loadSchools() {
      try {
        setSchoolsLoading(true);
        const response = await getSchoolsForDropdown();
        setSchools(response.schools || []);
      } catch (err) {
        console.error("LOAD SCHOOLS ERROR:", err);
        setStudentError("Okul listesi yüklenemedi.");
      } finally {
        setSchoolsLoading(false);
      }
    }

    loadSchools();
  }, []);

  useEffect(() => {
    const match = findPathMatch(menuGroups, location.pathname);
    if (match?.groupKey) {
      setOpenGroups((prev) => ({ ...prev, [match.groupKey]: true }));
    }
  }, [location.pathname, menuGroups]);

  useEffect(() => {
    if (!isMobile) setIsSidebarOpen(false);
  }, [isMobile]);

  function handleFileChange(e) {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setError("");
    setResult(null);
  }

  async function handleImport() {
    setError("");
    setResult(null);

    if (!file) {
      setError("Lütfen önce bir Excel dosyası seçin.");
      return;
    }

    try {
      setLoading(true);
      const response = await importUsersFromFile(file);
      setResult(response.report);
    } catch (err) {
      console.error("UI IMPORT ERROR:", err);
      setError(err.message || "Import sırasında hata oluştu.");
    } finally {
      setLoading(false);
    }
  }

  async function handleStudentSearch() {
    setStudentError("");
    setUpdateMessage("");
    setStudentInfo(null);

    if (!selectedSchoolCode) {
      setStudentError("Lütfen bir okul seçin.");
      return;
    }

    if (!studentNumber.trim()) {
      setStudentError("Lütfen öğrenci numarasını girin.");
      return;
    }

    try {
      setSearchLoading(true);

      const response = await findStudentBySchoolAndNumber(
        selectedSchoolCode,
        studentNumber.trim()
      );

      setStudentInfo(response.student);
    } catch (err) {
      console.error("STUDENT SEARCH ERROR:", err);
      setStudentError(err.message || "Öğrenci bulunamadı.");
    } finally {
      setSearchLoading(false);
    }
  }

  function handleStudentFieldChange(field, value) {
    setStudentInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleArrayFieldChange(field, value) {
    const normalized = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    setStudentInfo((prev) => ({
      ...prev,
      [field]: normalized,
    }));
  }

  async function handleStudentUpdate() {
    if (!studentInfo?.uid) {
      setStudentError("Güncellenecek öğrenci bulunamadı.");
      return;
    }

    setStudentError("");
    setUpdateMessage("");

    try {
      setUpdateLoading(true);

      const payload = {
        displayName: studentInfo.displayName || "",
        email: studentInfo.email || "",
        campusCode: studentInfo.campusCode || "",
        campusName: studentInfo.campusName || "",
        schoolCode: studentInfo.schoolCode || "",
        schoolName: studentInfo.schoolName || "",
        schoolLevel: studentInfo.schoolLevel || "",
        classCodes: studentInfo.classCodes || [],
        subjectCodes: studentInfo.subjectCodes || [],
        studentNumber: studentInfo.studentNumber || "",
        tckn: studentInfo.tckn || "",
        phone: studentInfo.phone || "",
        isActive: studentInfo.isActive === true,
      };

      await updateStudentByUid(studentInfo.uid, payload);
      setUpdateMessage("Öğrenci bilgileri güncellendi.");
    } catch (err) {
      console.error("STUDENT UPDATE ERROR:", err);
      setStudentError(err.message || "Öğrenci güncellenemedi.");
    } finally {
      setUpdateLoading(false);
    }
  }

  const closeSidebar = () => setIsSidebarOpen(false);

  const isExpanded = isMobile ? true : isSidebarHovered;

  const sidebarStyle = isMobile
    ? {
        ...styles.mobileTopMenu,
        ...(isSidebarOpen ? styles.mobileTopMenuOpen : {}),
      }
    : {
        ...styles.sidebarDesktop,
        width: isExpanded ? "300px" : "96px",
      };

  const contentAreaStyle = isMobile
    ? {
        ...styles.contentArea,
        marginLeft: 0,
      }
    : {
        ...styles.contentArea,
        marginLeft: isExpanded ? "300px" : "96px",
        transition: "margin-left 0.28s ease",
      };

  const hasActiveDescendant = (node) => {
    if (node.path && node.path === location.pathname) return true;
    if (!node.children) return false;
    return node.children.some(hasActiveDescendant);
  };

  const renderChild = (child) => {
    const active = child.path === location.pathname;
    const ChildIcon = child.icon || Settings;

    return (
      <button
        key={`${child.label}-${child.path}`}
        style={{
          ...styles.subMenuItem,
          ...(active ? styles.subMenuItemActive : {}),
          paddingLeft: 14,
        }}
        onClick={() => {
          navigate(child.path);
          if (isMobile) closeSidebar();
        }}
      >
        <ChildIcon size={16} strokeWidth={2.1} style={styles.subMenuIcon} />
        <span style={styles.subMenuText}>{child.label}</span>
      </button>
    );
  };

  return (
    <div style={styles.page}>
      <Header
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
        onMenuToggle={() => setIsSidebarOpen((prev) => !prev)}
        mobileBgColor={PRIMARY_BLUE}
        mobileLeftCompact={true}
      />

      {isMobile && isSidebarOpen && (
        <div style={styles.mobileOverlay} onClick={closeSidebar} />
      )}

      <main
        style={{
          ...styles.main,
          ...(isMobile ? styles.mainMobile : {}),
        }}
      >
        <aside
          style={sidebarStyle}
          onMouseEnter={() => !isMobile && setIsSidebarHovered(true)}
          onMouseLeave={() => !isMobile && setIsSidebarHovered(false)}
        >
          {!isMobile && (
            <div style={styles.sidebarTop}>
              <div style={styles.logoContainer}>
                <img
                  src={itkLogo}
                  alt="Akademi Analitiği Logo"
                  style={styles.logo}
                />
              </div>
            </div>
          )}

          <div
            style={
              isMobile ? styles.mobileTopMenuScroll : styles.sidebarScroll
            }
          >
            {menuGroups.map((group) => {
              const active = hasActiveDescendant(group);
              const isOpen = !!openGroups[group.key];
              const GroupIcon = group.icon;

              return (
                <div key={group.key} style={styles.groupWrap}>
                  <button
                    style={{
                      ...styles.groupButton,
                      ...(active ? styles.groupButtonActive : {}),
                      justifyContent: isExpanded ? "space-between" : "center",
                    }}
                    onClick={() =>
                      setOpenGroups((prev) => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      }))
                    }
                    title={group.title}
                  >
                    <div style={styles.groupButtonLeft}>
                      <GroupIcon
                        size={20}
                        strokeWidth={2.1}
                        style={styles.groupIcon}
                      />
                      {isExpanded && (
                        <span style={styles.groupTitle}>{group.title}</span>
                      )}
                    </div>

                    {isExpanded && (
                      <ChevronDown
                        size={15}
                        strokeWidth={2.2}
                        style={{
                          ...styles.chevronIcon,
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    )}
                  </button>

                  {isExpanded && isOpen && (
                    <div style={styles.subMenuPanel}>
                      {group.children.map((child) => renderChild(child))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <section style={contentAreaStyle}>
          <div
            style={{
              ...styles.contentContainer,
              ...(isMobile ? styles.contentContainerMobile : {}),
            }}
          >
            <div
              style={{
                ...styles.cardsGrid,
                ...(isMobile ? styles.cardsGridMobile : {}),
              }}
            >
              <section
                style={{
                  ...styles.card,
                  ...(isMobile ? styles.cardMobile : {}),
                }}
              >
                <div style={styles.cardGlow} />
                <div style={styles.cardTopLine} />

                <div style={styles.sectionHeader}>
                  <div style={styles.headingBlock}>
                    <div style={styles.titleBadge}>Toplu İşlem</div>
                    <h2 style={styles.title}>Kullanıcı İçe Aktarma</h2>
                    <p style={styles.subtitle}>
                      Excel dosyasından toplu kullanıcı yükleyin.
                    </p>
                  </div>
                </div>

                <div style={styles.importBox}>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChange}
                    style={styles.fileInput}
                  />

                  {file && (
                    <p style={styles.fileInfo}>
                      Seçilen dosya: <strong>{file.name}</strong>
                    </p>
                  )}

                  <div style={styles.buttonRow}>
                    <button
                      type="button"
                      onClick={handleImport}
                      disabled={loading}
                      style={{
                        ...styles.primaryButton,
                        ...(loading ? styles.buttonDisabled : {}),
                        ...(isMobile ? styles.primaryButtonMobile : {}),
                      }}
                    >
                      {loading ? "İçe aktarılıyor..." : "İçe Aktar"}
                    </button>
                  </div>

                  {error && <p style={styles.error}>{error}</p>}

                  {result && (
                    <div style={styles.resultBox}>
                      <h3 style={styles.resultTitle}>Import Sonucu</h3>
                      <div
                        style={{
                          ...styles.resultStats,
                          ...(isMobile ? styles.resultStatsMobile : {}),
                        }}
                      >
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Toplam Satır</span>
                          <strong style={styles.statValue}>
                            {result.totalRows}
                          </strong>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Başarılı</span>
                          <strong style={styles.statValue}>
                            {result.successCount}
                          </strong>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statLabel}>Başarısız</span>
                          <strong style={styles.statValue}>
                            {result.failedCount}
                          </strong>
                        </div>
                      </div>

                      <pre style={styles.pre}>
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </section>

              <section
                style={{
                  ...styles.card,
                  ...(isMobile ? styles.cardMobile : {}),
                }}
              >
                <div style={styles.cardGlowSecondary} />
                <div style={styles.cardTopLine} />

                <div style={styles.sectionHeader}>
                  <div style={styles.headingBlock}>
                    <div style={styles.titleBadgeSecondary}>Manuel Güncelleme</div>
                    <h2 style={styles.title}>Öğrenci Ara ve Güncelle</h2>
                    <p style={styles.subtitle}>
                      Okul seçip öğrenci numarasına göre arama yapın, sonra
                      bilgileri güncelleyin.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.searchGrid,
                    ...(isMobile ? styles.searchGridMobile : {}),
                  }}
                >
                  <div style={styles.fieldBlock}>
                    <label style={styles.label}>Okul</label>
                    <select
                      value={selectedSchoolCode}
                      onChange={(e) => setSelectedSchoolCode(e.target.value)}
                      style={styles.input}
                      disabled={schoolsLoading}
                    >
                      <option value="">
                        {schoolsLoading ? "Okullar yükleniyor..." : "Okul seçin"}
                      </option>

                      {schools.map((school) => (
                        <option
                          key={school.code || school.id}
                          value={school.code}
                        >
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.fieldBlock}>
                    <label style={styles.label}>Öğrenci Numarası</label>
                    <input
                      type="text"
                      placeholder="Örn: 12345"
                      value={studentNumber}
                      onChange={(e) => setStudentNumber(e.target.value)}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.searchButtonWrap}>
                    <button
                      type="button"
                      onClick={handleStudentSearch}
                      disabled={searchLoading}
                      style={{
                        ...styles.primaryButton,
                        ...(searchLoading ? styles.buttonDisabled : {}),
                        ...(isMobile ? styles.primaryButtonMobile : {}),
                      }}
                    >
                      {searchLoading ? "Aranıyor..." : "Ara"}
                    </button>
                  </div>
                </div>

                {studentError && <p style={styles.error}>{studentError}</p>}
                {updateMessage && (
                  <p style={styles.success}>{updateMessage}</p>
                )}

                {studentInfo && (
                  <div
                    style={{
                      ...styles.formGrid,
                      ...(isMobile ? styles.formGridMobile : {}),
                    }}
                  >
                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>UID</label>
                      <input
                        value={studentInfo.uid || ""}
                        disabled
                        style={{ ...styles.input, ...styles.readonlyInput }}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>TCKN</label>
                      <input
                        value={studentInfo.tckn || ""}
                        onChange={(e) =>
                          handleStudentFieldChange("tckn", e.target.value)
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Öğrenci Numarası</label>
                      <input
                        value={studentInfo.studentNumber || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "studentNumber",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Ad Soyad</label>
                      <input
                        value={studentInfo.displayName || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "displayName",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Email</label>
                      <input
                        value={studentInfo.email || ""}
                        onChange={(e) =>
                          handleStudentFieldChange("email", e.target.value)
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Campus Code</label>
                      <input
                        value={studentInfo.campusCode || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "campusCode",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Campus Name</label>
                      <input
                        value={studentInfo.campusName || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "campusName",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>School Code</label>
                      <input
                        value={studentInfo.schoolCode || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "schoolCode",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>School Name</label>
                      <input
                        value={studentInfo.schoolName || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "schoolName",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>School Level</label>
                      <input
                        value={studentInfo.schoolLevel || ""}
                        onChange={(e) =>
                          handleStudentFieldChange(
                            "schoolLevel",
                            e.target.value
                          )
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Telefon</label>
                      <input
                        value={studentInfo.phone || ""}
                        onChange={(e) =>
                          handleStudentFieldChange("phone", e.target.value)
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Class Codes</label>
                      <input
                        value={(studentInfo.classCodes || []).join(",")}
                        onChange={(e) =>
                          handleArrayFieldChange("classCodes", e.target.value)
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={styles.fieldBlock}>
                      <label style={styles.label}>Subject Codes</label>
                      <input
                        value={(studentInfo.subjectCodes || []).join(",")}
                        onChange={(e) =>
                          handleArrayFieldChange("subjectCodes", e.target.value)
                        }
                        style={styles.input}
                      />
                    </div>

                    <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
                      <label style={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={studentInfo.isActive === true}
                          onChange={(e) =>
                            handleStudentFieldChange(
                              "isActive",
                              e.target.checked
                            )
                          }
                        />
                        Aktif
                      </label>
                    </div>

                    <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
                      <button
                        type="button"
                        onClick={handleStudentUpdate}
                        disabled={updateLoading}
                        style={{
                          ...styles.primaryButton,
                          ...(updateLoading ? styles.buttonDisabled : {}),
                          ...(isMobile ? styles.primaryButtonMobile : {}),
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        {updateLoading
                          ? "Güncelleniyor..."
                          : "Öğrenciyi Güncelle"}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: BG,
    fontFamily: TYPO.fontFamily,
    display: "flex",
    flexDirection: "column",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
    overflowX: "hidden",
  },

  main: {
    flex: 1,
    display: "flex",
    position: "relative",
    padding: "24px",
    paddingTop: "24px",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
    background: BG,
    minWidth: 0,
  },

  mainMobile: {
    padding: "0",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
  },

  sidebarDesktop: {
    position: "fixed",
    top: "0",
    left: 0,
    bottom: 0,
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "24px 0 48px rgba(15, 23, 42, 0.24), 8px 0 18px rgba(15, 23, 42, 0.14)",
    transition: "width 0.28s ease",
    zIndex: 40,
    overflow: "hidden",
  },

  mobileTopMenu: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: "16px",
    right: "16px",
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRadius: 16,
    boxShadow:
      "0 18px 40px rgba(15, 23, 42, 0.22), 0 6px 18px rgba(15, 23, 42, 0.14)",
    zIndex: 70,
    overflow: "hidden",
    opacity: 0,
    transform: "translateY(-12px)",
    pointerEvents: "none",
    transition: "all 0.22s ease",
  },

  mobileTopMenuOpen: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },

  sidebarTop: {
    minHeight: "72px",
    padding: "12px 10px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid rgba(255,255,255,0.14)",
    background: PRIMARY_BLUE,
  },

  logoContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 0",
  },

  logo: {
    width: "48px",
    height: "48px",
    objectFit: "contain",
    borderRadius: "14px",
    background: "#ffffff",
    padding: "6px",
  },

  sidebarScroll: {
    height: "calc(100% - 72px)",
    overflowY: "auto",
    padding: "12px 10px 18px",
  },

  mobileTopMenuScroll: {
    padding: "12px",
  },

  groupWrap: {
    marginBottom: 10,
  },

  groupButton: {
    width: "100%",
    minHeight: 48,
    border: "none",
    background: "transparent",
    borderRadius: 12,
    color: "#ffffff",
    padding: "0 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
  },

  groupButtonActive: {
    background: "rgba(255,255,255,0.16)",
    color: "#ffffff",
    boxShadow: "none",
  },

  groupButtonLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  groupIcon: {
    flexShrink: 0,
    color: "#ffffff",
  },

  groupTitle: {
    fontSize: TYPO.sizes.md,
    fontWeight: TYPO.weights.semibold,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.tight,
  },

  chevronIcon: {
    color: "#ffffff",
    transition: "transform 0.2s ease",
    flexShrink: 0,
  },

  subMenuPanel: {
    marginTop: 8,
    marginLeft: 8,
    paddingLeft: 10,
    borderLeft: "2px solid rgba(255,255,255,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  subMenuItem: {
    minHeight: 40,
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.normal,
    transition: "all 0.2s ease",
  },

  subMenuItemActive: {
    background: "#ffffff",
    color: PRIMARY_BLUE,
    fontWeight: TYPO.weights.semibold,
  },

  subMenuIcon: {
    flexShrink: 0,
  },

  subMenuText: {
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    lineHeight: TYPO.lineHeights.normal,
    letterSpacing: TYPO.letterSpacing.normal,
  },

  contentArea: {
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    background: "transparent",
    borderRadius: 0,
    padding: 0,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 0,
    overflowX: "hidden",
  },

  contentContainer: {
    width: "100%",
    maxWidth: "100%",
    background: "transparent",
    borderRadius: 20,
    padding: "0",
    minHeight: "calc(100vh - 168px)",
    boxShadow: "none",
    border: "none",
    boxSizing: "border-box",
    minWidth: 0,
    overflowX: "hidden",
  },

  contentContainerMobile: {
    padding: "0 6px 10px",
  },

  cardsGrid: {
    display: "grid",
    gap: 18,
    minWidth: 0,
  },

  cardsGridMobile: {
    gap: 12,
  },

  card: {
    position: "relative",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    padding: 18,
    borderRadius: 24,
    boxShadow:
      "0 20px 45px rgba(15, 23, 42, 0.10), 0 8px 20px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.70)",
    border: `1px solid ${BORDER}`,
    minWidth: 0,
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  cardMobile: {
    padding: "14px 12px",
    borderRadius: 16,
    boxShadow:
      "0 14px 28px rgba(15, 23, 42, 0.08), 0 6px 16px rgba(15, 23, 42, 0.05), inset 0 1px 0 rgba(255,255,255,0.68)",
  },

  cardGlow: {
    position: "absolute",
    top: "-70px",
    right: "-45px",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(21,101,192,0.16) 0%, rgba(21,101,192,0) 72%)",
    pointerEvents: "none",
  },

  cardGlowSecondary: {
    position: "absolute",
    top: "-65px",
    right: "-30px",
    width: 170,
    height: 170,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.12) 0%, rgba(37,99,235,0) 72%)",
    pointerEvents: "none",
  },

  cardTopLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background:
      "linear-gradient(90deg, rgba(21,101,192,0.95) 0%, rgba(59,130,246,0.65) 55%, rgba(191,219,254,0.2) 100%)",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },

  headingBlock: {
    minWidth: 0,
    width: "100%",
  },

  titleBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(21,101,192,0.08)",
    color: PRIMARY_BLUE,
    border: "1px solid rgba(21,101,192,0.12)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    marginBottom: 10,
  },

  titleBadgeSecondary: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,0.08)",
    color: "#1d4ed8",
    border: "1px solid rgba(37,99,235,0.12)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    marginBottom: 10,
  },

  title: {
    margin: 0,
    color: PRIMARY_BLUE,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    wordBreak: "break-word",
  },

  subtitle: {
    margin: "6px 0 0 0",
    color: TEXT_MUTED,
    fontSize: 14,
    lineHeight: 1.6,
    wordBreak: "break-word",
  },

  importBox: {
    display: "grid",
    gap: 14,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },

  fileInput: {
    fontSize: 14,
    width: "100%",
    maxWidth: "100%",
    padding: "12px 14px",
    borderRadius: 14,
    border: `1px dashed rgba(21,101,192,0.24)`,
    background: "rgba(248,250,252,0.95)",
    boxSizing: "border-box",
  },

  fileInfo: {
    margin: 0,
    color: TEXT_MUTED,
    fontSize: 13,
    wordBreak: "break-word",
  },

  buttonRow: {
    display: "flex",
    justifyContent: "flex-start",
    minWidth: 0,
  },

  primaryButton: {
    minHeight: 44,
    padding: "0 18px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #1565C0 0%, #1e88e5 100%)",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow:
      "0 10px 22px rgba(21, 101, 192, 0.22), inset 0 1px 0 rgba(255,255,255,0.24)",
    transition: "all 0.2s ease",
    maxWidth: "100%",
  },

  primaryButtonMobile: {
    width: "100%",
    minHeight: 46,
    justifyContent: "center",
  },

  buttonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  searchGrid: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr auto",
    gap: 14,
    alignItems: "end",
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },

  searchGridMobile: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },

  searchButtonWrap: {
    display: "flex",
    alignItems: "end",
    minWidth: 0,
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginTop: 18,
    minWidth: 0,
    position: "relative",
    zIndex: 1,
  },

  formGridMobile: {
    gridTemplateColumns: "1fr",
    gap: 12,
    marginTop: 16,
  },

  fieldBlock: {
    minWidth: 0,
    width: "100%",
  },

  label: {
    display: "block",
    marginBottom: 7,
    fontWeight: 600,
    color: TEXT_DARK,
    fontSize: 13,
    wordBreak: "break-word",
  },

  input: {
    width: "100%",
    minHeight: 44,
    padding: "10px 12px",
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    boxSizing: "border-box",
    fontSize: 14,
    color: TEXT_DARK,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    minWidth: 0,
    maxWidth: "100%",
    boxShadow: "inset 0 1px 2px rgba(15,23,42,0.03)",
  },

  readonlyInput: {
    background: "#f8fafc",
    color: "#475569",
  },

  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 600,
    color: TEXT_DARK,
    flexWrap: "wrap",
    fontSize: 14,
  },

  error: {
    color: "crimson",
    marginTop: 4,
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: "break-word",
    position: "relative",
    zIndex: 1,
  },

  success: {
    color: "green",
    marginTop: 4,
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: "break-word",
    position: "relative",
    zIndex: 1,
  },

  resultBox: {
    marginTop: 8,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    padding: 14,
    borderRadius: 16,
    border: `1px solid ${BORDER}`,
    minWidth: 0,
    overflowX: "hidden",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },

  resultTitle: {
    marginTop: 0,
    marginBottom: 12,
    fontSize: 17,
    color: TEXT_DARK,
    wordBreak: "break-word",
  },

  resultStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginBottom: 16,
    minWidth: 0,
  },

  resultStatsMobile: {
    gridTemplateColumns: "1fr",
    gap: 10,
  },

  statItem: {
    background: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: 12,
    minWidth: 0,
    boxShadow:
      "0 6px 14px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,0.75)",
  },

  statLabel: {
    display: "block",
    fontSize: 12,
    color: TEXT_MUTED,
    marginBottom: 6,
    wordBreak: "break-word",
  },

  statValue: {
    color: TEXT_DARK,
    fontSize: 18,
    fontWeight: 700,
  },

  pre: {
    margin: 0,
    background: "#fff",
    padding: 12,
    borderRadius: 14,
    overflowX: "auto",
    border: `1px solid ${BORDER}`,
    fontSize: 12,
    maxWidth: "100%",
    boxSizing: "border-box",
    lineHeight: 1.5,
  },

  mobileOverlay: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.18)",
    zIndex: 65,
  },
};